import { useEffect, useState, useMemo } from 'react'
import { useUserStore } from '@/stores/userStore'
import { useCycleStore, type ActiveCycleCompound } from '@/stores/cycleStore'
import { COMPOUND_SEED_DATA, type CompoundSeedData } from '@/data/compounds'
import {
  calculateBloodLevels,
  generateDoseSchedule,
  type InjectionFrequency,
  type CompoundCurve,
  combineCompoundCurves,
  getPKParameters,
} from '@/lib/pharmacokinetics'
import { cn } from '@/lib/utils'

// UI Components
import {
  EditIcon,
  DeleteIcon,
  CheckIcon,
  FlaskIcon,
  IconButton,
} from '@/components/ui/Icons'

// Injection site rotation
const INJECTION_SITES = [
  'Left Glute',
  'Right Glute',
  'Left Quad',
  'Right Quad',
  'Left Delt',
  'Right Delt',
  'Left VG',
  'Right VG',
  'Left Lat',
  'Right Lat',
]

function CycleSelector() {
  const { cycles, activeCycle, setActiveCycle, createCycle, deleteCycle } = useCycleStore()
  const { profile } = useUserStore()
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [cycleType, setCycleType] = useState<'blast' | 'cruise' | 'pct'>('blast')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const handleCreate = async () => {
    if (!profile?.id || !newName.trim()) return

    const today = new Date().toISOString().split('T')[0]
    await createCycle(profile.id, newName.trim(), cycleType, today)
    setNewName('')
    setShowCreate(false)
  }

  const handleActivate = async (cycleId: string) => {
    // setActiveCycle now handles deactivating the previous cycle and activating the new one
    await setActiveCycle(cycleId)
  }

  const handleDelete = async (cycleId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (deleteConfirm === cycleId) {
      await deleteCycle(cycleId)
      setDeleteConfirm(null)
    } else {
      setDeleteConfirm(cycleId)
      // Auto-cancel confirmation after 3 seconds
      setTimeout(() => setDeleteConfirm(null), 3000)
    }
  }

  // Sort cycles: active first, then by date
  const sortedCycles = [...cycles].sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1
    if (b.status === 'active' && a.status !== 'active') return 1
    return b.created_at.localeCompare(a.created_at)
  })

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium text-text-primary">Cycles</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-sm text-primary"
        >
          {showCreate ? 'Cancel' : '+ New Cycle'}
        </button>
      </div>

      {showCreate && (
        <div className="mb-4 p-3 bg-elevated rounded-lg space-y-3">
          <input
            type="text"
            placeholder="Cycle name (e.g., Winter Blast 2024)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full p-2 bg-surface border border-border rounded-lg text-text-primary"
          />
          <div className="flex gap-2">
            {(['blast', 'cruise', 'pct'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setCycleType(type)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium ${
                  cycleType === type
                    ? type === 'blast'
                      ? 'bg-primary text-white'
                      : type === 'cruise'
                      ? 'bg-accent text-white'
                      : 'bg-success text-white'
                    : 'bg-surface text-text-secondary'
                }`}
              >
                {type.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            onClick={handleCreate}
            disabled={!newName.trim()}
            className="w-full py-2 bg-primary text-white rounded-lg font-medium disabled:opacity-50"
          >
            Create Cycle
          </button>
        </div>
      )}

      {cycles.length === 0 ? (
        <div className="text-center py-8">
          <FlaskIcon className="w-8 h-8 mx-auto mb-3 text-text-muted opacity-30" />
          <p className="text-sm text-text-muted">No cycles yet</p>
          <p className="text-xs text-text-muted mt-1">Create one to start planning</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedCycles.map((cycle) => {
            const isActive = cycle.status === 'active'
            const isSelected = activeCycle?.id === cycle.id

            return (
              <div
                key={cycle.id}
                onClick={() => handleActivate(cycle.id)}
                className={cn(
                  'w-full p-3 rounded-xl text-left transition-all cursor-pointer group',
                  isSelected
                    ? 'bg-primary/10 ring-1 ring-primary shadow-sm'
                    : isActive
                    ? 'bg-success/5 ring-1 ring-success/30'
                    : 'bg-elevated hover:bg-elevated/80 active:scale-[0.99]'
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-text-primary truncate">{cycle.name}</p>
                      {isActive && (
                        <span className="flex-shrink-0 w-2 h-2 bg-success rounded-full animate-pulse" title="Active cycle" />
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-0.5">
                      <span className="font-medium">{cycle.cycle_type.toUpperCase()}</span>
                      <span className="mx-1.5">·</span>
                      <span>{cycle.start_date}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span
                      className={cn(
                        'px-2 py-0.5 text-[10px] font-medium rounded-full uppercase',
                        isActive
                          ? 'bg-success/20 text-success'
                          : cycle.status === 'planning'
                          ? 'bg-warning/20 text-warning'
                          : 'bg-elevated text-text-muted'
                      )}
                    >
                      {cycle.status}
                    </span>
                    <IconButton
                      icon={deleteConfirm === cycle.id ? <CheckIcon className="w-4 h-4" /> : <DeleteIcon className="w-4 h-4" />}
                      label={deleteConfirm === cycle.id ? 'Confirm delete' : 'Delete cycle'}
                      variant="danger"
                      size="sm"
                      onClick={(e) => handleDelete(cycle.id, e)}
                      className={cn(
                        'opacity-60 group-hover:opacity-100 transition-opacity',
                        deleteConfirm === cycle.id && 'bg-error/20 opacity-100'
                      )}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CompoundSelector({ onAdd }: { onAdd: (compound: CompoundSeedData) => void }) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<'all' | 'steroid' | 'ancillary' | 'peptide'>('all')

  const filtered = useMemo(() => {
    return COMPOUND_SEED_DATA.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.shortName.toLowerCase().includes(search.toLowerCase())
      const matchesCategory = category === 'all' || c.category === category
      return matchesSearch && matchesCategory
    })
  }, [search, category])

  return (
    <div className="space-y-3">
      <input
        type="text"
        placeholder="Search compounds..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full p-2 bg-surface border border-border rounded-lg text-text-primary"
      />

      <div className="flex gap-2">
        {(['all', 'steroid', 'ancillary', 'peptide'] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              category === cat
                ? 'bg-primary text-white'
                : 'bg-elevated text-text-secondary'
            }`}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      <div className="max-h-48 overflow-y-auto space-y-1">
        {filtered.slice(0, 15).map((compound) => (
          <button
            key={compound.name}
            onClick={() => onAdd(compound)}
            className="w-full p-2 text-left bg-elevated hover:bg-surface rounded-lg transition-colors"
          >
            <p className="font-medium text-text-primary text-sm">{compound.shortName}</p>
            <p className="text-xs text-text-muted">{compound.name}</p>
          </button>
        ))}
      </div>
    </div>
  )
}

function AddCompoundModal({
  isOpen,
  onClose,
  cycleId,
}: {
  isOpen: boolean
  onClose: () => void
  cycleId: string
}) {
  const { addCompoundToCycle } = useCycleStore()
  const [selectedCompound, setSelectedCompound] = useState<CompoundSeedData | null>(null)
  const [dose, setDose] = useState('')
  const [frequency, setFrequency] = useState<InjectionFrequency>('2x_week')
  const [startWeek, setStartWeek] = useState('1')
  const [endWeek, setEndWeek] = useState('12')

  if (!isOpen) return null

  const handleAdd = async () => {
    if (!selectedCompound || !dose) return

    await addCompoundToCycle(
      cycleId,
      selectedCompound.name,
      parseFloat(dose),
      frequency,
      parseInt(startWeek),
      parseInt(endWeek)
    )

    setSelectedCompound(null)
    setDose('')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold text-text-primary">Add Compound</h2>
        </div>

        <div className="p-4 space-y-4">
          {!selectedCompound ? (
            <CompoundSelector onAdd={setSelectedCompound} />
          ) : (
            <>
              <div className="p-3 bg-elevated rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-text-primary">{selectedCompound.shortName}</p>
                    <p className="text-xs text-text-muted">{selectedCompound.name}</p>
                  </div>
                  <button
                    onClick={() => setSelectedCompound(null)}
                    className="text-sm text-primary"
                  >
                    Change
                  </button>
                </div>
                <div className="mt-2 text-xs text-text-muted">
                  Half-life: {selectedCompound.halfLifeDays.toFixed(1)} days •{' '}
                  {selectedCompound.route}
                </div>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1">
                  Dose per injection (mg)
                </label>
                <input
                  type="number"
                  value={dose}
                  onChange={(e) => setDose(e.target.value)}
                  placeholder={selectedCompound.typicalDoseRangeMg}
                  className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
                />
                <p className="text-xs text-text-muted mt-1">
                  Typical range: {selectedCompound.typicalDoseRangeMg}mg
                </p>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1">Frequency</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as InjectionFrequency)}
                  className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
                >
                  <option value="daily">Daily</option>
                  <option value="eod">Every Other Day</option>
                  <option value="e3d">Every 3 Days</option>
                  <option value="3x_week">3x per Week (MWF)</option>
                  <option value="2x_week">2x per Week</option>
                  <option value="1x_week">Once per Week</option>
                  <option value="e10d">Every 10 Days</option>
                  <option value="e14d">Every 2 Weeks</option>
                </select>
                <p className="text-xs text-text-muted mt-1">
                  Recommended: {selectedCompound.recommendedFrequency}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Start Week</label>
                  <input
                    type="number"
                    value={startWeek}
                    onChange={(e) => setStartWeek(e.target.value)}
                    min="1"
                    className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">End Week</label>
                  <input
                    type="number"
                    value={endWeek}
                    onChange={(e) => setEndWeek(e.target.value)}
                    min="1"
                    className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-elevated text-text-secondary rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedCompound || !dose}
            className="flex-1 py-2 bg-primary text-white rounded-lg font-medium disabled:opacity-50"
          >
            Add Compound
          </button>
        </div>
      </div>
    </div>
  )
}

function EditCompoundModal({
  isOpen,
  onClose,
  compound,
}: {
  isOpen: boolean
  onClose: () => void
  compound: ActiveCycleCompound
}) {
  const { updateCycleCompound } = useCycleStore()
  const [dose, setDose] = useState(compound.doseMg.toString())
  const [frequency, setFrequency] = useState<InjectionFrequency>(compound.frequency as InjectionFrequency)
  const [startWeek, setStartWeek] = useState(compound.startWeek.toString())
  const [endWeek, setEndWeek] = useState(compound.endWeek.toString())

  if (!isOpen) return null

  const handleSave = async () => {
    await updateCycleCompound(compound.id, {
      doseMg: parseFloat(dose),
      frequency,
      startWeek: parseInt(startWeek),
      endWeek: parseInt(endWeek),
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl w-full max-w-md">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold text-text-primary">Edit {compound.compound.shortName}</h2>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">
              Dose per injection (mg)
            </label>
            <input
              type="number"
              value={dose}
              onChange={(e) => setDose(e.target.value)}
              className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as InjectionFrequency)}
              className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
            >
              <option value="daily">Daily</option>
              <option value="eod">Every Other Day</option>
              <option value="e3d">Every 3 Days</option>
              <option value="3x_week">3x per Week (MWF)</option>
              <option value="2x_week">2x per Week</option>
              <option value="1x_week">Once per Week</option>
              <option value="e10d">Every 10 Days</option>
              <option value="e14d">Every 2 Weeks</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Start Week</label>
              <input
                type="number"
                value={startWeek}
                onChange={(e) => setStartWeek(e.target.value)}
                min="1"
                className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">End Week</label>
              <input
                type="number"
                value={endWeek}
                onChange={(e) => setEndWeek(e.target.value)}
                min="1"
                className="w-full p-2 bg-elevated border border-border rounded-lg text-text-primary"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-elevated text-text-secondary rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!dose}
            className="flex-1 py-2 bg-primary text-white rounded-lg font-medium disabled:opacity-50"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

function CycleCompoundsList() {
  const { activeCycle, cycleCompounds, removeCompoundFromCycle } = useCycleStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCompound, setEditingCompound] = useState<ActiveCycleCompound | null>(null)

  if (!activeCycle) {
    return (
      <div className="card text-center py-8">
        <p className="text-text-muted">Select a cycle to add compounds</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium text-text-primary">Compounds</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="text-sm text-primary"
        >
          + Add
        </button>
      </div>

      {cycleCompounds.length === 0 ? (
        <div className="text-center py-8">
          <FlaskIcon className="w-8 h-8 mx-auto mb-3 text-text-muted opacity-30" />
          <p className="text-sm text-text-muted">No compounds added yet</p>
          <p className="text-xs text-text-muted mt-1">Add your first compound to see blood levels</p>
        </div>
      ) : (
        <div className="space-y-2">
          {cycleCompounds.map((cc) => (
            <div
              key={cc.id}
              className="p-3 bg-elevated rounded-xl hover:bg-elevated/80 transition-colors group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-text-primary">{cc.compound.shortName}</p>
                    {/* Compound info badges */}
                    <div className="flex gap-1">
                      {cc.compound.aromatizes && (
                        <span className="px-1.5 py-0.5 bg-warning/20 text-warning text-[10px] rounded-full font-medium">
                          Arom
                        </span>
                      )}
                      {cc.compound.is19Nor && (
                        <span className="px-1.5 py-0.5 bg-error/20 text-error text-[10px] rounded-full font-medium">
                          19-Nor
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary mt-0.5">
                    <span className="font-medium">{cc.doseMg}mg</span>
                    <span className="text-text-muted mx-1">·</span>
                    <span>{cc.frequency.replace('_', '/')}</span>
                    <span className="text-text-muted mx-1">·</span>
                    <span className="text-text-muted">Wk {cc.startWeek}-{cc.endWeek}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <IconButton
                    icon={<EditIcon className="w-4 h-4" />}
                    label="Edit compound"
                    variant="subtle"
                    size="sm"
                    onClick={() => setEditingCompound(cc)}
                  />
                  <IconButton
                    icon={<DeleteIcon className="w-4 h-4" />}
                    label="Remove compound"
                    variant="danger"
                    size="sm"
                    onClick={() => removeCompoundFromCycle(cc.id)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddCompoundModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        cycleId={activeCycle.id}
      />

      {editingCompound && (
        <EditCompoundModal
          isOpen={!!editingCompound}
          onClose={() => setEditingCompound(null)}
          compound={editingCompound}
        />
      )}
    </div>
  )
}

// Colors for different compounds
const COMPOUND_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
]

function BloodLevelChart() {
  const { cycleCompounds } = useCycleStore()

  const { chartData, compoundNames } = useMemo(() => {
    if (cycleCompounds.length === 0) return { chartData: null, compoundNames: [] }

    const names: string[] = []
    const curves: CompoundCurve[] = cycleCompounds.map((cc) => {
      const frequencyMap: Record<string, InjectionFrequency> = {
        daily: 'daily',
        eod: 'eod',
        e3d: 'e3d',
        '3x_week': '3x_week',
        '3x/week': '3x_week',
        '2x_week': '2x_week',
        '2x/week': '2x_week',
        '1x_week': '1x_week',
        '1x/week': '1x_week',
        e10d: 'e10d',
        e14d: 'e14d',
      }

      const freq = frequencyMap[cc.frequency] || '2x_week'
      const startDay = (cc.startWeek - 1) * 7
      const endDay = cc.endWeek * 7

      const doses = generateDoseSchedule(cc.doseMg, freq, startDay, endDay)

      // Get proper PK parameters for this compound
      const pkParams = getPKParameters(
        cc.compound.name,
        cc.compound.halfLifeDays,
        cc.compound.bioavailability ?? undefined
      )

      const levels = calculateBloodLevels(
        doses,
        pkParams,
        endDay + 28 // Extra 4 weeks to show clearance
      )

      names.push(cc.compound.shortName)
      return {
        compoundName: cc.compound.shortName,
        levels,
      }
    })

    return { chartData: combineCompoundCurves(curves), compoundNames: names }
  }, [cycleCompounds])

  if (!chartData || chartData.length === 0) {
    return (
      <div className="card">
        <h2 className="font-medium text-text-primary mb-3">Blood Level Chart</h2>
        <div className="h-48 flex items-center justify-center bg-elevated rounded-lg">
          <p className="text-text-muted text-sm">Add compounds to see blood levels</p>
        </div>
      </div>
    )
  }

  // Find max level for scaling (use total for consistent scale)
  const maxLevel = Math.max(...chartData.map((d) => d.total))

  // Generate SVG path - larger viewBox for finer detail
  const width = 600
  const height = 200
  const paddingLeft = 16
  const paddingRight = 16
  const paddingTop = 12
  const paddingBottom = 24
  const chartWidth = width - paddingLeft - paddingRight
  const chartHeight = height - paddingTop - paddingBottom

  // Get the actual max day from the chart data (not the array length)
  const maxDay = chartData[chartData.length - 1]?.day ?? 1

  const xScale = (day: number) => paddingLeft + (day / maxDay) * chartWidth
  const yScale = (level: number) => paddingTop + chartHeight - (level / maxLevel) * chartHeight

  // Generate path for total (combined) curve
  const totalPathD = chartData
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.day)} ${yScale(d.total)}`)
    .join(' ')

  // Generate paths for individual compounds
  const compoundPaths = compoundNames.map((name) => {
    const pathD = chartData
      .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.day)} ${yScale(d.byCompound[name] || 0)}`)
      .join(' ')
    return { name, pathD }
  })

  // Week markers - show every week or every 2 weeks if cycle is long
  const totalWeeks = Math.ceil(maxDay / 7)
  const weekInterval = totalWeeks > 12 ? 2 : 1
  const weekMarkers = Array.from(
    { length: Math.ceil(totalWeeks / weekInterval) + 1 },
    (_, i) => i * weekInterval * 7
  ).filter((d) => d <= maxDay)

  // Y-axis levels
  const yAxisLevels = [0, 0.25, 0.5, 0.75, 1]

  const showTotal = compoundNames.length > 1

  return (
    <div className="card">
      <h2 className="font-medium text-text-primary mb-3">Blood Level Chart</h2>

      <div className="bg-elevated rounded-lg p-3">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {/* Grid lines */}
          {yAxisLevels.slice(1).map((ratio) => (
            <line
              key={ratio}
              x1={paddingLeft}
              y1={yScale(maxLevel * ratio)}
              x2={width - paddingRight}
              y2={yScale(maxLevel * ratio)}
              stroke="currentColor"
              strokeOpacity={0.08}
              strokeWidth={0.5}
              strokeDasharray="4,4"
            />
          ))}

          {/* Y-axis label - relative scale indicator */}
          <text
            x={8}
            y={paddingTop + 8}
            textAnchor="start"
            className="fill-current opacity-30"
            style={{ fontSize: '7px' }}
          >
            Concentration
          </text>

          {/* X-axis baseline */}
          <line
            x1={paddingLeft}
            y1={height - paddingBottom}
            x2={width - paddingRight}
            y2={height - paddingBottom}
            stroke="currentColor"
            strokeOpacity={0.15}
            strokeWidth={0.5}
          />

          {/* Week markers */}
          {weekMarkers.map((day) => (
            <g key={day}>
              <line
                x1={xScale(day)}
                y1={height - paddingBottom}
                x2={xScale(day)}
                y2={height - paddingBottom + 3}
                stroke="currentColor"
                strokeOpacity={0.25}
                strokeWidth={0.5}
              />
              <text
                x={xScale(day)}
                y={height - paddingBottom + 12}
                textAnchor="middle"
                className="fill-current opacity-40"
                style={{ fontSize: '8px' }}
              >
                W{day / 7}
              </text>
            </g>
          ))}

          {/* Gradient definitions for each compound */}
          <defs>
            {compoundPaths.map((cp, idx) => (
              <linearGradient key={cp.name} id={`gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COMPOUND_COLORS[idx % COMPOUND_COLORS.length]} stopOpacity={0.2} />
                <stop offset="100%" stopColor={COMPOUND_COLORS[idx % COMPOUND_COLORS.length]} stopOpacity={0.02} />
              </linearGradient>
            ))}
            {showTotal && (
              <linearGradient id="gradient-total" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6B7280" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#6B7280" stopOpacity={0.02} />
              </linearGradient>
            )}
          </defs>

          {/* Fill under total curve (if multiple compounds) */}
          {showTotal && (
            <path
              d={`${totalPathD} L ${xScale(chartData.length - 1)} ${height - paddingBottom} L ${paddingLeft} ${height - paddingBottom} Z`}
              fill="url(#gradient-total)"
            />
          )}

          {/* Fill under individual compound curves */}
          {compoundPaths.map((cp, idx) => (
            <path
              key={`fill-${cp.name}`}
              d={`${cp.pathD} L ${xScale(chartData.length - 1)} ${height - paddingBottom} L ${paddingLeft} ${height - paddingBottom} Z`}
              fill={`url(#gradient-${idx})`}
            />
          ))}

          {/* Total curve (dashed, if multiple compounds) */}
          {showTotal && (
            <path
              d={totalPathD}
              fill="none"
              stroke="#6B7280"
              strokeWidth={1}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="4,2"
              opacity={0.6}
            />
          )}

          {/* Individual compound curves */}
          {compoundPaths.map((cp, idx) => (
            <path
              key={cp.name}
              d={cp.pathD}
              fill="none"
              stroke={COMPOUND_COLORS[idx % COMPOUND_COLORS.length]}
              strokeWidth={1.25}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {cycleCompounds.map((cc, idx) => (
          <span
            key={cc.id}
            className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1"
            style={{
              backgroundColor: `${COMPOUND_COLORS[idx % COMPOUND_COLORS.length]}15`,
              color: COMPOUND_COLORS[idx % COMPOUND_COLORS.length]
            }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: COMPOUND_COLORS[idx % COMPOUND_COLORS.length] }}
            />
            {cc.compound.shortName}
          </span>
        ))}
        {showTotal && (
          <span className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1 bg-gray-500/10 text-gray-500">
            <span className="w-2 h-0.5 bg-gray-500" style={{ borderTop: '1px dashed' }} />
            Total
          </span>
        )}
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border">
        <div className="text-center">
          <p className="text-xs text-text-muted">Weekly Dose</p>
          <p className="text-sm font-medium text-text-primary">
            {cycleCompounds.reduce((sum, cc) => {
              const freqMap: Record<string, number> = {
                'daily': 7, 'eod': 3.5, 'e3d': 2.33, '3x_week': 3,
                '2x_week': 2, '1x_week': 1, 'e10d': 0.7, 'e14d': 0.5
              }
              return sum + cc.doseMg * (freqMap[cc.frequency] || 1)
            }, 0).toLocaleString()} mg
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted">Duration</p>
          <p className="text-sm font-medium text-text-primary">{Math.ceil(chartData.length / 7)} weeks</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted">Compounds</p>
          <p className="text-sm font-medium text-text-primary">{cycleCompounds.length}</p>
        </div>
      </div>
    </div>
  )
}

function InjectionLogger() {
  const { profile } = useUserStore()
  const { activeCycle, logInjection, injections, loadInjections, getCompoundByName } =
    useCycleStore()
  const [showForm, setShowForm] = useState(false)
  const [compoundName, setCompoundName] = useState('')
  const [dose, setDose] = useState('')
  const [site, setSite] = useState(INJECTION_SITES[0])
  const [oilVolume, setOilVolume] = useState('')

  useEffect(() => {
    if (profile?.id) {
      loadInjections(profile.id, activeCycle?.id)
    }
  }, [profile?.id, activeCycle?.id, loadInjections])

  const handleLog = async () => {
    if (!profile?.id || !compoundName || !dose) return

    await logInjection(
      profile.id,
      compoundName,
      parseFloat(dose),
      site,
      oilVolume ? parseFloat(oilVolume) : undefined,
      undefined,
      activeCycle?.id
    )

    setCompoundName('')
    setDose('')
    setOilVolume('')
    setShowForm(false)
  }

  // Get next suggested site based on rotation
  const lastSite = injections[0]?.site
  const suggestedSiteIndex = lastSite
    ? (INJECTION_SITES.indexOf(lastSite) + 1) % INJECTION_SITES.length
    : 0
  const suggestedSite = INJECTION_SITES[suggestedSiteIndex]

  // Available compounds from the cycle
  const availableCompounds = activeCycle
    ? useCycleStore.getState().cycleCompounds.map((cc) => cc.compound)
    : COMPOUND_SEED_DATA.filter((c) => c.route === 'injectable').slice(0, 10)

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium text-text-primary">Injection Log</h2>
        <button
          onClick={() => {
            setSite(suggestedSite)
            setShowForm(!showForm)
          }}
          className="text-sm text-primary"
        >
          {showForm ? 'Cancel' : '+ Log Injection'}
        </button>
      </div>

      {showForm && (
        <div className="mb-4 p-3 bg-elevated rounded-lg space-y-3">
          <select
            value={compoundName}
            onChange={(e) => {
              setCompoundName(e.target.value)
              const compound = getCompoundByName(e.target.value)
              if (compound) {
                // Auto-suggest dose from typical range
                const match = compound.typicalDoseRangeMg.match(/\d+/)
                if (match) setDose(match[0])
              }
            }}
            className="w-full p-2 bg-surface border border-border rounded-lg text-text-primary"
          >
            <option value="">Select compound...</option>
            {availableCompounds.map((c) => (
              <option key={c.name} value={c.name}>
                {c.shortName} ({c.name})
              </option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-muted mb-1">Dose (mg)</label>
              <input
                type="number"
                value={dose}
                onChange={(e) => setDose(e.target.value)}
                className="w-full p-2 bg-surface border border-border rounded-lg text-text-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">Oil Volume (ml)</label>
              <input
                type="number"
                step="0.1"
                value={oilVolume}
                onChange={(e) => setOilVolume(e.target.value)}
                className="w-full p-2 bg-surface border border-border rounded-lg text-text-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-text-muted mb-1">
              Injection Site (Next: {suggestedSite})
            </label>
            <select
              value={site}
              onChange={(e) => setSite(e.target.value)}
              className="w-full p-2 bg-surface border border-border rounded-lg text-text-primary"
            >
              {INJECTION_SITES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleLog}
            disabled={!compoundName || !dose}
            className="w-full py-2 bg-primary text-white rounded-lg font-medium disabled:opacity-50"
          >
            Log Injection
          </button>
        </div>
      )}

      {injections.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-4">No injections logged yet</p>
      ) : (
        <div className="space-y-2">
          {injections.slice(0, 10).map((inj) => (
            <div key={inj.id} className="flex items-center justify-between p-2 bg-elevated rounded-lg">
              <div>
                <p className="text-sm font-medium text-text-primary">{inj.compoundName}</p>
                <p className="text-xs text-text-muted">
                  {inj.doseMg}mg • {inj.site || 'N/A'}
                </p>
              </div>
              <p className="text-xs text-text-muted">
                {new Date(inj.date).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CompoundWarnings() {
  const { cycleCompounds } = useCycleStore()

  const warnings = useMemo(() => {
    const warns: { type: 'warning' | 'info'; message: string }[] = []

    // Check for 19-nors without test base
    const has19Nor = cycleCompounds.some((cc) => cc.compound.is19Nor)
    const hasTestosterone = cycleCompounds.some(
      (cc) => cc.compound.compoundClass === 'testosterone'
    )

    if (has19Nor && !hasTestosterone) {
      warns.push({
        type: 'warning',
        message: '19-Nor detected without testosterone base. Consider adding a test base.',
      })
    }

    // Check for aromatizing compounds without AI
    const hasAromatizing = cycleCompounds.some(
      (cc) => cc.compound.aromatizes && cc.compound.aromatizationRate !== 'none'
    )
    const hasAI = cycleCompounds.some((cc) => cc.compound.compoundClass === 'ai')

    if (hasAromatizing && !hasAI) {
      warns.push({
        type: 'info',
        message: 'Aromatizing compound detected. Consider having an AI on hand.',
      })
    }

    // Check for hepatotoxic orals
    const hepatotoxicOrals = cycleCompounds.filter(
      (cc) => cc.compound.hepatotoxicity === 'high' && cc.compound.route === 'oral'
    )

    if (hepatotoxicOrals.length > 1) {
      warns.push({
        type: 'warning',
        message: 'Multiple hepatotoxic orals. Consider limiting to one at a time.',
      })
    }

    // Check for prolactin management with 19-nors
    const hasTren = cycleCompounds.some((cc) =>
      cc.compound.name.toLowerCase().includes('trenbolone')
    )
    const hasCaber = cycleCompounds.some((cc) =>
      cc.compound.name.toLowerCase().includes('cabergoline')
    )

    if (hasTren && !hasCaber) {
      warns.push({
        type: 'info',
        message: 'Trenbolone can elevate prolactin. Consider P5P or Caber.',
      })
    }

    return warns
  }, [cycleCompounds])

  if (warnings.length === 0) return null

  return (
    <div className="card space-y-2">
      <h2 className="font-medium text-text-primary mb-2">Interaction Warnings</h2>
      {warnings.map((w, i) => (
        <div
          key={i}
          className={`p-3 rounded-lg ${
            w.type === 'warning' ? 'bg-warning/10' : 'bg-primary/10'
          }`}
        >
          <p
            className={`text-sm ${
              w.type === 'warning' ? 'text-warning' : 'text-primary'
            }`}
          >
            {w.message}
          </p>
        </div>
      ))}
    </div>
  )
}

export default function PEDPlanner() {
  const { profile } = useUserStore()
  const { loadCycles } = useCycleStore()

  useEffect(() => {
    if (profile?.id) {
      loadCycles(profile.id)
    }
  }, [profile?.id, loadCycles])

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Header */}
      <div className="card">
        <h1 className="text-xl font-bold text-text-primary mb-2">Cycle Planner</h1>
        <p className="text-text-secondary text-sm">
          Plan, track, and visualize your cycles with pharmacokinetic modeling.
        </p>
      </div>

      <CycleSelector />
      <CycleCompoundsList />
      <CompoundWarnings />
      <BloodLevelChart />
      <InjectionLogger />
    </div>
  )
}
